// Organization Map — the derivation layer. Pure functions, no React, no I/O.
//
// WHY THIS FILE EXISTS SEPARATELY
// ==============================
// The Organization Map cannot be verified against live data overnight:
// `/api/organization/*` is `require_founder` AND behind `CIPHER_ORGANIZATION_V1`
// (default off, 404 when off). So the only honest way to prove the surface is
// correct is to prove the *derivation* is correct against a fixture, and to
// prove the component renders whatever the derivation returns. That is what
// `__tests__/orgModel.test.js` does.
//
// NO DEPARTMENT NAME APPEARS IN THIS FILE, OR ANYWHERE IN THE RENDERING PATH.
// The eleven ratified departments are canonical in
// `docs/engineering/cipher-organizational-model-v1.md` §3, and they arrive from
// the API on each agent's `department` field. If the API ever disagrees with the
// ratified list, this surface renders what the API said and the difference is a
// finding for the log — never something to paper over with a hardcoded list
// (that is the instruction, and it is also the only way the map can stay honest
// about what the organization actually contains).
//
// `GET /organization/summary` returns `departments` as a COUNT, not a list, so
// the department axis is derived by grouping `GET /organization/agents`.

/** Lifecycle values the API uses. Not department names — these are states. */
export const LIFECYCLE_ORDER = [
  "live",
  "dormant",
  "suspended",
  "retired",
];

/** Layer values, ordered Founder-outward. Not department names. */
export const LAYER_ORDER = ["brain", "head", "specialist", "tool"];

function orderIndex(list, value) {
  const i = list.indexOf(String(value || "").toLowerCase());
  return i === -1 ? list.length : i;
}

/**
 * Group an agents payload into departments.
 *
 * Accepts the raw `{ count, agents: [...] }` body, or a bare array, or null —
 * the last of which is the normal case tonight, since the flag is off.
 *
 * Returns `[{ id, label, agents, total, byLifecycle, byLayer }]`, ordered by
 * agent count descending then label, so the busiest department reads first and
 * the ordering is stable for a given payload. Agents whose `department` is
 * missing collect under a single unnamed group rather than being dropped —
 * silently losing an agent would be worse than showing one that needs a label.
 */
export function deriveDepartments(payload) {
  const agents = normalizeAgents(payload);
  const groups = new Map();

  for (const agent of agents) {
    const id = String(agent.department || "").trim();
    if (!groups.has(id)) {
      groups.set(id, { id, label: id, agents: [] });
    }
    groups.get(id).agents.push(agent);
  }

  const out = [];
  for (const group of groups.values()) {
    out.push({
      ...group,
      total: group.agents.length,
      byLifecycle: countBy(group.agents, "lifecycle"),
      byLayer: countBy(group.agents, "layer"),
      agents: [...group.agents].sort(
        (a, b) =>
          orderIndex(LAYER_ORDER, a.layer) - orderIndex(LAYER_ORDER, b.layer) ||
          String(a.title || "").localeCompare(String(b.title || ""))
      ),
    });
  }

  return out.sort(
    (a, b) => b.total - a.total || a.label.localeCompare(b.label)
  );
}

/** Tolerate `{agents:[...]}`, a bare array, or nothing at all. */
export function normalizeAgents(payload) {
  if (Array.isArray(payload)) return payload.filter(Boolean);
  if (payload && Array.isArray(payload.agents)) {
    return payload.agents.filter(Boolean);
  }
  return [];
}

function countBy(rows, key) {
  const counts = {};
  for (const row of rows) {
    const value = String(row[key] || "").trim() || "unspecified";
    counts[value] = (counts[value] || 0) + 1;
  }
  return counts;
}

/**
 * Roster totals across every department.
 *
 * Derived from the same agents payload rather than read from
 * `/organization/summary`, so the headline and the departments below it can
 * never disagree — one payload, one set of numbers.
 */
export function deriveRoster(payload) {
  const agents = normalizeAgents(payload);
  return {
    total: agents.length,
    departments: deriveDepartments(payload).length,
    byLifecycle: countBy(agents, "lifecycle"),
    byLayer: countBy(agents, "layer"),
  };
}

/**
 * The fields the Agent Profile drawer shows, in order.
 *
 * This list is the whole of what the agent record carries. It deliberately has
 * no utilization, spend or outcome entry: correction 5 in
 * `design/system/CIPHER_BUILD_INSTRUCTION.md` establishes that no such field
 * exists on the record — absent, not empty — and that a component which reads
 * one at all is a bug. Nothing in the orchestration layer writes to
 * `organization_agents`, so no future run will fill it either.
 */
export const PROFILE_FIELDS = [
  { key: "agent_id", label: "Agent ID", mono: true },
  { key: "layer", label: "Layer" },
  { key: "title", label: "Title" },
  { key: "purpose", label: "Purpose", wide: true },
  { key: "department", label: "Department" },
  { key: "reports_to", label: "Reports to", mono: true },
  { key: "lifecycle", label: "Lifecycle" },
  { key: "readiness", label: "Readiness" },
  { key: "provenance", label: "Provenance", wide: true },
  { key: "granted_tools", label: "Granted tools", list: true },
  { key: "skills", label: "Skills", list: true },
  { key: "activation_package", label: "Activation package", wide: true },
  { key: "created_at", label: "Created", mono: true },
  { key: "updated_at", label: "Updated", mono: true },
];

/**
 * Read one profile field off an agent, tolerating the several shapes a record
 * may legitimately use for the same thing.
 *
 * `reports_to` is the one worth naming: the record may express reporting as
 * `reports_to`, `reporting`, or a nested `reporting.to`. Guessing wrong renders
 * a blank where a real value exists, which is exactly the failure mode the
 * empty state is supposed to distinguish itself from.
 */
export function readField(agent, key) {
  if (!agent) return undefined;
  if (agent[key] !== undefined && agent[key] !== null) return agent[key];
  if (key === "reports_to") {
    if (agent.reporting && typeof agent.reporting === "object") {
      return agent.reporting.to ?? agent.reporting.reports_to;
    }
    return agent.reporting;
  }
  if (key === "activation_package") return agent.activation ?? agent.package;
  return undefined;
}

/** True when a field has nothing worth rendering — drives the empty state. */
export function isBlank(value) {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

/**
 * Map a `useBrainResource` result onto the surface state to render.
 *
 * Five states, not three, because the two failure modes this surface will
 * actually hit tonight are indistinguishable if you only handle "error":
 *
 *   disabled  404 — `CIPHER_ORGANIZATION_V1` is off, so the surface genuinely
 *             does not exist yet. Not a fault.
 *   denied    403 — `require_founder` refused. The server is the gate (R5); the
 *             nav hiding the tab is not access control, so a non-founder who
 *             types the URL must land here.
 *   empty     200 with no agents — materialization has not run.
 *   error     anything else.
 *   ok        agents present.
 */
export function resolveState(resource) {
  if (!resource) return "loading";
  if (resource.status === "loading") return "loading";
  if (resource.status === "disabled") return "disabled";
  if (resource.status === "error") {
    const code =
      resource.error &&
      resource.error.response &&
      resource.error.response.status;
    if (code === 403) return "denied";
    if (code === 404) return "disabled";
    return "error";
  }
  if (normalizeAgents(resource.data).length === 0) return "empty";
  return "ok";
}
